import { SafePromise } from '@benzinga/safe-await';
import { IncludeHeader, RestfulClient, Session } from '@benzinga/session';
import { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';
import {
  BreakingNewsResponse,
  ArticleSearchResponse,
  ArticleData,
  StoryObject,
  ChartbeatPost,
  ChartbeatParams,
  SearchResultResponse,
  SearchResultsParams,
} from '../entities';
import { BasicNewsEnvironment } from '../environment';
import { removeFalsyFromQueryParams } from '@benzinga/utils';

const SIMPLE_QUERY_TIMEOUT_MS = 4500;

export class BasicNewsRestful extends RestfulClient {
  constructor(session: Session, includeHeaders?: IncludeHeader) {
    super(session.getEnvironment(BasicNewsEnvironment).url, session, includeHeaders);
  }

  getArticle = (nodeId: number | string): SafePromise<ArticleData> => {
    const url = this.URL(`/api/articles/${nodeId}/camel`);
    return this.get(url, { allowsAnonymousAuth: true });
  };

  simplyQueryNews = async (
    query: SimpleNewsQuery = {},
    options: SimpleNewsOptions = {},
  ): SafePromise<StoryObject[]> => {
    const params = new URLSearchParams();
    const queryParams = { ...query, ...options };
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (!value) return;
        params.append(key, value);
      });
    }
    const hostname = this.session.getEnvironment(BasicNewsEnvironment).simplyQueryUrl;
    const url = this.URL(`${hostname}api/news?${params}`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
      resilience: {
        delayMultiple: 3000,
        delayOffset: 3000,
        maxDelay: 45000,
        maxNumOfAttempts: 1,
      },
      timeout: SIMPLE_QUERY_TIMEOUT_MS,
    });
  };

  getBreakingNews = (): SafePromise<BreakingNewsResponse> => {
    const hostname = this.session.getEnvironment(BasicNewsEnvironment).editorialToolsUrl;
    const url = this.URL(`${hostname}breaking-events`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      cache: 'no-store',
      credentials: 'same-origin',
    });
  };

  getLatestArticles = (query: string): SafePromise<ArticleSearchResponse> => {
    const url = this.URL(`ajax-cache/marketfy/latest/${query}`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
      mode: 'cors',
    });
  };

  getTopPostsUsingChartbeat = async (params: ChartbeatParams = {}): SafePromise<{ pages: ChartbeatPost[] }> => {
    const queryParams = {
      host: 'benzinga.com',
      limit: '10',
      ...params,
    } as { [key: string]: string };

    const cleanedParams = removeFalsyFromQueryParams(new URLSearchParams(queryParams).toString());
    const hostname = this.session.getEnvironment(BasicNewsEnvironment).chartBeatUrl;
    const url = this.URL(`${hostname}api/chartbeat?${cleanedParams}`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
    });
  };

  getSearchResults = async (params: SearchResultsParams): SafePromise<SearchResultResponse> => {
    const hostname = this.session.getEnvironment(BasicNewsEnvironment).simplyQueryUrl;
    const url = this.URL(`${hostname}api-next/search`, {
      include_articles: params.include_articles,
      include_symbols: params.include_symbols,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      q: params.query,
      return_limit: params.return_limit,
      symbol_limit: params.symbol_limit,
    });
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
    });
  };
}
